import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { Box, Button, Dialog, DialogActions, DialogTitle, Stack } from '@mui/material';
import { GridDensity, GridToolbarContainer } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { ChangeEvent, FC, memo, useContext, useEffect, useRef, useState } from 'react';
import { ToolbarActionGroup } from 'src/components/ui/actionGroup/actionGroupButtons';
import { CreateButton } from 'src/components/ui/actionGroup/CreateButton';
import { DeleteButton } from 'src/components/ui/actionGroup/DeleteButton';
import { ExportButton } from 'src/components/ui/actionGroup/ExportButton';
import { FileImportButton } from 'src/components/ui/actionGroup/FileImportButton';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import { queryKey } from 'src/hooks/useTrucks.request';
import { saveAs } from 'src/utils/fileSaver';

import MyGridToolbarColumnsButton from '../common/MyGridToolbarColumnsButton';
import MyGridToolbarDensitySelector from '../common/MyGridToolbarDensitySelector';
import FilterPanelToolbarFilterButton from '../common/MyGridToolbarFilterButton';

const CustomToolbar: FC<{
  toggleDialogIsOpen: () => void;
  onClickDeleteTrucks: () => void;
  setIsLoading: (prevState: boolean) => boolean;
  canDeleteAll: boolean;
  deleteAll: (settledFnk: () => void) => void;
  handleDownloadCSV: () => void;
  density: GridDensity;
  onDensityChange: (density: GridDensity) => void;
}> = memo((
  {
    toggleDialogIsOpen,
    onClickDeleteTrucks,
    setIsLoading,
    canDeleteAll,
    deleteAll,
    handleDownloadCSV,
    density,
    onDensityChange
  }
  ) => {
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [importFile, setImportFile] = useState<File | undefined>(undefined);
  const importButtonOnClick = (event: ChangeEvent<HTMLInputElement>) => {
    setImportFile(event.target.files[0]);
  };

  useEffect(() => {
    if (!importFile) return;

    const requestPath = '/api/v2/trucks/import';

    const data = new FormData();
    data.append('file', importFile);

    setIsLoading(true);
    enqueueSnackbar('インポートを開始します');

    axios
      .post(requestPath, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(
        (response: AxiosResponse<{ severity: string; message: string }>) => {
          enqueueSnackbar(response.data.message);
        }
      )
      .catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.response.status === 400) {
          enqueueSnackbar(
            '取り込めない車両がありました、データをご確認ください。'
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
          const blob = new Blob([e.response.data], {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
            type: e.response.data.type
          });
          saveAs(blob, 'errors.txt');
        } else {
          enqueueSnackbar(fiveZeroZeroErrorMessage);
        }
      })
      .finally(() => {
        setImportFile(undefined);
        setIsLoading(false);
        void queryClient.invalidateQueries({
          queryKey: [queryKey]
        });
        if (importRef) {
          importRef.current.value = '';
        }
      });
  }, [enqueueSnackbar, importFile]);

  const [disableImport, setDisableImport] = useState<boolean>(false);
  const [importTooltip, setImportTooltip] = useState<string>('');
  const [deleteHandlerDialogIsOpen, setDeleteHandlerDialogIsOpen] = useState<boolean>(false);

  const importRef = useRef<HTMLInputElement>(null);

  const deleteHandlerDialogOnClose = () => {
    setDeleteHandlerDialogIsOpen(false);
  };

  const deleteBtnOnClick = () => {
    if (canDeleteAll) {
      setDeleteHandlerDialogIsOpen(true);

      return;
    }

    onClickDeleteTrucks();
    setDeleteHandlerDialogIsOpen(false);
  };

  useEffect(() => {
    if (licenseContext?.config?.selected_company_id === 0) {
      setDisableImport(true);
      setImportTooltip('インポート対象の事業所を選択してください');
    } else {
      setDisableImport(false);
      setImportTooltip('');
    }
  }, [licenseContext?.config]);

  return (
    <>
      <GridToolbarContainer
        sx={{
          width: '100%'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterPanelToolbarFilterButton />
            <MyGridToolbarColumnsButton />
            <MyGridToolbarDensitySelector
              density={density}
              onDensityChange={onDensityChange}
            />
          </Stack>
          <ToolbarActionGroup>
            <FileImportButton
              disabled={disableImport}
              tooltipTitle={importTooltip}
              onChange={importButtonOnClick}
              inputRef={importRef}
            />
            <ExportButton onClick={handleDownloadCSV} label="エクスポート" />
            <DeleteButton onClick={deleteBtnOnClick} label="トラック削除" />
            <CreateButton onClick={toggleDialogIsOpen} label="トラック追加" />
          </ToolbarActionGroup>
        </Box>
      </GridToolbarContainer>
      <Dialog
        open={deleteHandlerDialogIsOpen}
        onClose={deleteHandlerDialogOnClose}
      >
        <DialogTitle>
          削除する対象を選択してください
        </DialogTitle>
        <DialogActions>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteForeverRoundedIcon />}
            onClick={() => {
              deleteAll(deleteHandlerDialogOnClose);
            }}
          >
            すべて削除する
          </Button>
          <Button
            onClick={() => {
              onClickDeleteTrucks();
              setDeleteHandlerDialogIsOpen(false);
            }}
            variant="outlined"
            startIcon={<DeleteSweepRoundedIcon />}
          >
            選択したものを削除する
          </Button>
          <Button
            onClick={deleteHandlerDialogOnClose}
            startIcon={<HighlightOffRoundedIcon />}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default CustomToolbar;
